/*
  Боковая навигация — §4.4.

  Десять разделов, глубина не больше двух уровней.

  08.09.2026: пустых разделов больше нет - радар, коммуникации, исполнение и
  отчёты наполнены. Признак `soon` оставлен намеренно: правило прежнее - раздел,
  которого ещё нет, не убирается из меню и не выдаёт ошибку, а открывается
  страницей с объяснением, что там появится и когда. Так человек видит очертания
  продукта целиком и не ищет раздел, которого «почему-то нет».

  Разделы, закрытые для роли, из меню убираются совсем: оператору коммуникаций
  незачем видеть дверь, за которую ему нельзя (§3.1.5).

  08.09.2026, узкий экран. До правки на телефоне те же десять разделов лежали
  полосой сверху и занимали треть экрана: до заголовка страницы человек
  прокручивал пол-экрана, и так в каждом разделе. Теперь на узком экране сверху
  остаётся полоса с названием и кнопкой, а разделы выезжают панелью слева - той
  же тёмной колонкой, что на большом экране, чтобы человек не переучивался.

  Где что решается: ЧТО показать - здесь, ПРИ КАКОЙ ширине - медиазапросом в
  `styles.adaptive.css`. Разметка на всех экранах одна, ширину клиент не
  измеряет: измерение дало бы второй ответ на вопрос, который уже решён
  оформлением, и они разошлись бы при первой правке порога.
*/

import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'

import { BrandMark, SectionIcon } from './Icons.jsx'
import { allows, SECTIONS } from './sections.js'

export default function Sidebar({ profile, burning, onLogout }) {
  const [open, setOpen] = useState(false)
  // Раздел открыт, если у роли есть ХОТЯ БЫ одно из перечисленных разрешений. Перечень
  // разделов общий с охранником маршрутов (`sections.js`, `RouteGate.jsx`).
  const allowed = (section) => allows(profile, section.need)

  // Esc закрывает панель. Слушатель висит на документе, а не на самой панели:
  // клавиша нажимается, когда внимание человека где угодно на странице.
  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Пока панель открыта, страница под ней не прокручивается: иначе палец,
  // промахнувшись мимо пункта, уносит содержимое под панелью, и человек
  // возвращается в раздел, прокрученный неизвестно куда.
  useEffect(() => {
    if (!open) return undefined
    const was = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = was
    }
  }, [open])

  return (
    <>
      <div className="topbar">
        <button
          type="button"
          className="topbar__toggle"
          aria-expanded={open}
          aria-controls="side-nav"
          onClick={() => setOpen((shown) => !shown)}
        >
          {/* Три полосы - общепринятый знак меню; читалке экрана он не нужен,
              ей достаточно подписи кнопки, поэтому знак от неё скрыт. */}
          <span className="topbar__bars" aria-hidden="true" />
          Меню
        </button>
        {/*
          Названия организации здесь нет намеренно. Оно уже есть в панели
          разделов, и второй его показ означал бы ДВА одинаковых узла в
          разметке: на узком экране виден один, на широком другой, а читалка
          экрана и проверки видят оба. Организация у человека одна и та же весь
          день - место в полосе телефона стоит дороже.
        */}
        <div className="topbar__brand">
          <BrandMark />
          Арбитр ЖКХ
        </div>
      </div>

      {open ? (
        <div
          className="side__veil"
          data-testid="side-veil"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      ) : null}

      <nav id="side-nav" className={open ? 'side side--open' : 'side'} aria-label="Разделы">
        <div className="side__brand">
          <BrandMark />
          Арбитр ЖКХ
        </div>
        <div className="side__org">{profile.tenant_name}</div>

        {SECTIONS.filter(allowed).map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            // Переход закрывает панель: иначе она накроет собой раздел, в
            // который человек только что перешёл, и раздел покажется пустым.
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              [
                'side__link',
                isActive ? 'side__link--active' : '',
                section.soon ? 'side__link--muted' : '',
              ]
                .filter(Boolean)
                .join(' ')
            }
          >
            <span className="side__link-text">
              <SectionIcon name={section.icon} />
              {section.title}
            </span>
            {section.badge === 'burning' && burning > 0 ? (
              <span className="side__badge" title="Счетов, по которым горит срок давности">
                {burning}
              </span>
            ) : null}
          </NavLink>
        ))}

        <div className="side__foot">
          {/* Цвет и отступы классами, а не `style`: тот же оттенок уже записан
              палитрой, и при её смене захардкоженный разошёлся бы с ней. */}
          <div className="side__user">{profile.full_name}</div>
          <div className="side__role">{profile.role_title}</div>
          {profile.is_readonly ? (
            <div className="tag tag--warn side__readonly">только чтение</div>
          ) : null}
          <button type="button" className="button button--quiet" onClick={onLogout}>
            Выйти
          </button>
        </div>
      </nav>
    </>
  )
}
